import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MdImage,
  MdDescription,
  MdViewInAr,
  MdScatterPlot,
  MdCode,
  MdNotes,
  MdOpenInFull,
} from 'react-icons/md';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import {
  ModelViewer,
  ModelViewerModal,
  getModelLoaderType,
  isCadFile,
  modelOf,
  type ModelChoice,
} from '@aibuild/model-viewer';
import type { ProcessArtifactDto } from '@api/processes';
import { RenderErrorBoundary } from '@components/ErrorBoundary';
import { canPreviewPdf } from '@components/PdfViewer/canPreviewPdf';
import { useModelBlob } from '@hooks/useModelBlob';
import { useOptionalEditorContext } from '../useEditorContext';
import {
  DocumentPreviewSurface,
  ExpandButton,
  Meta,
  ModelLoadingSurface,
  ModelPreviewSurface,
  Name,
  Placeholder,
  PreviewBox,
  TextLoadingSurface,
  TextPreviewBox,
  Thumb,
  TopRow,
  Wrap,
} from './ArtifactPreview.styled';

const PdfPreview = lazy(() => import('@components/PdfViewer/PdfPreview'));

const ICON_BY_TYPE: Record<string, React.ReactNode> = {
  IMAGE: <MdImage />,
  MODEL_3D: <MdViewInAr />,
  POINT_CLOUD: <MdScatterPlot />,
  GCODE: <MdCode />,
  TEXT: <MdNotes />,
  DOCUMENT: <MdDescription />,
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

// Inline-load only models up to this size — larger files would block the UI on
// the blob fetch and the GPU on geometry upload. The chat-side viewer enforces
// the same 20 MB threshold, so users get consistent behaviour across products.
const MODEL_INLINE_PREVIEW_LIMIT = 20 * 1024 * 1024;

// Cap inline text previews to 256 KB. Most "text input" values are short
// user-typed strings; we still want to render a multi-paragraph file
// without blowing up the renderer, but a multi-MB log would only show its
// first screenful anyway — fall back to the placeholder + download link.
const TEXT_INLINE_PREVIEW_LIMIT = 256 * 1024;

// Common point-cloud file extensions. We sniff the file name because the
// MIME type for these formats is usually `application/octet-stream`, which
// is too generic to map onto a specific icon.
const POINT_CLOUD_EXTENSIONS = ['.ply', '.pcd', '.las', '.laz', '.xyz'];

const isPointCloud = (contentType: string, name: string): boolean => {
  if (contentType.includes('point-cloud') || contentType.includes('pointcloud')) {
    return true;
  }
  const lower = name.toLowerCase();
  return POINT_CLOUD_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const portTypeFromContent = (contentType: string, name: string): string => {
  if (contentType.startsWith('image/')) return 'IMAGE';
  if (isPointCloud(contentType, name)) return 'POINT_CLOUD';
  if (getModelLoaderType(name) !== null) return 'MODEL_3D';
  if (contentType.includes('stl') || contentType.includes('model')) {
    return 'MODEL_3D';
  }
  if (contentType.includes('gcode')) return 'GCODE';
  if (contentType.startsWith('text/')) return 'TEXT';
  return 'DOCUMENT';
};

const inferPortType = (artifact: ProcessArtifactDto, model: ModelChoice<string> | null): string =>
  model === null ? portTypeFromContent(artifact.contentType, artifact.name) : 'MODEL_3D';

// How long to show "Generating 3D preview…" before giving up and showing the
// icon placeholder. Covers the runner's 120 s conversion cap plus dispatch
// overhead. Preview generation is best-effort with no durable job state, so
// this timer — not a backend signal — is what guarantees the card settles.
const PREVIEW_GENERATION_TIMEOUT_MS = 130_000;

/**
 * True while a server-side preview is plausibly still being generated.
 * Flips to false once {@link PREVIEW_GENERATION_TIMEOUT_MS} elapses so every
 * silent-failure path (VM reaped, dispatch lost, control-plane restart) ends
 * at the placeholder rather than shimmering forever.
 */
function usePreviewGenerationPending(waiting: boolean): boolean {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(() => setExpired(true), PREVIEW_GENERATION_TIMEOUT_MS);
    // Clearing the expiry here (rather than at the top of the effect) keeps
    // this off react-hooks/set-state-in-effect while still resetting the
    // window whenever we start waiting again.
    return () => {
      clearTimeout(timer);
      setExpired(false);
    };
  }, [waiting]);
  return waiting && !expired;
}

/**
 * Fetch a presigned text artifact and expose its decoded contents. Used by
 * the inline TEXT preview branch to render an Input node's value (or any
 * other small text artifact) as a read-only textbox without making the user
 * download the file. Returns {@code null} text while in-flight and on error
 * — the caller falls back to the icon placeholder in either case so a
 * transient S3 hiccup doesn't strand the row.
 *
 * <p>Backed by React Query so cancellation, dedupe across mounts of the
 * same artifact, and the "reset state when the source URL changes" reset
 * fall out of the cache key automatically — no manual seenKey / cancelled
 * flag bookkeeping. When the gating flag is off (or the URL is missing)
 * the query is disabled and the hook returns the neutral
 * {@code text=null, loading=false, failed=false} shape so the caller's
 * conditional branches stay unchanged.</p>
 */
function useTextContent(
  url: string | undefined,
  shouldFetch: boolean,
): { text: string | null; loading: boolean; failed: boolean } {
  const enabled = Boolean(shouldFetch && url);
  const { data, isLoading, isError } = useQuery<string>({
    queryKey: ['artifactText', url],
    queryFn: async (): Promise<string> => {
      // The Boolean(enabled) gate above guarantees `url` is a string by
      // the time queryFn fires, but TypeScript can't see across the
      // hook boundary. Throwing on the unreachable branch makes the
      // type-narrowing explicit AND keeps a guard rail if the gating
      // logic ever regresses.
      if (!url) throw new Error('useTextContent: url is required');
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    },
    enabled,
    // Presigned URLs are immutable for their lifetime -- once we've
    // fetched the bytes for a given URL, the result is stable forever.
    // Mark fresh so React Query never auto-refetches behind our back.
    staleTime: Infinity,
    // Don't retry on transport failure: the caller's failure UX is the
    // icon placeholder, which the user already understands. A retry
    // storm against a flaky S3 only delays the fallback.
    retry: false,
  });
  if (!enabled) {
    return { text: null, loading: false, failed: false };
  }
  return {
    text: data ?? null,
    loading: isLoading,
    failed: isError,
  };
}

export interface ArtifactPreviewProps {
  artifact: ProcessArtifactDto;
  /** Optional explicit hint for the icon — falls back to contentType inference. */
  portType?: string;
  dataTestId?: string;
}

/**
 * Compact preview card for an artifact. Shows:
 *   - inline image thumbnail with click-to-fullscreen lightbox for IMAGE
 *   - inline interactive 3D viewer (three.js) with click-to-fullscreen modal
 *     for MODEL_3D (GLB/STL/OBJ/3MF) when the file is small enough
 *   - read-only inline textarea for TEXT / NUMBER (used by the Input node's
 *     "text input" preview as well as any other small text artifact)
 *   - typed icon placeholder for everything else
 *
 * The artifact name is always a download link so users can grab the file
 * directly from the node card.
 */
const ArtifactPreview: React.FC<ArtifactPreviewProps> = ({
  artifact,
  portType,
  dataTestId = 'artifact-preview',
}) => {
  // CAD formats (STEP/BREP) aren't browser-renderable, so the backend ships a
  // derived `previewUrl` we render instead; GLB/STL/OBJ/3MF load as they are.
  const model = modelOf({
    name: artifact.name,
    source: artifact.downloadUrl || null,
    preview: artifact.previewUrl ? { source: artifact.previewUrl, name: artifact.previewName } : null,
  });
  const modelSourceUrl = model?.source;

  const inferredType = portType ?? inferPortType(artifact, model);
  const isImage = inferredType === 'IMAGE';

  const is3DModel = inferredType === 'MODEL_3D' && model !== null;
  // Gate on the size of the asset we actually fetch: the derived preview's own
  // size, not the unrelated source CAD file's. A preview with no reported size
  // is treated as too large to inline.
  const previewByteSize =
    model?.from === 'file' ? artifact.fileSize : artifact.previewSizeBytes ?? null;
  const canPreviewModel =
    is3DModel && previewByteSize !== null && previewByteSize <= MODEL_INLINE_PREVIEW_LIMIT;

  // NUMBER inputs are persisted as TEXT artifacts on the wire, but a caller
  // may still pass {@code portType="NUMBER"} explicitly for a freshly-typed
  // value before the artifact lands — keep both in scope so the inline
  // preview behaves the same regardless of which signal arrived first.
  const isTextual = inferredType === 'TEXT' || inferredType === 'NUMBER';
  const canPreviewText =
    isTextual && !!artifact.downloadUrl && artifact.fileSize <= TEXT_INLINE_PREVIEW_LIMIT;

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);

  // Process artifact URLs are presigned by the control plane, so they are
  // fetched directly rather than through the authed API client.
  const { blob, failed: modelFailed } = useModelBlob(modelSourceUrl, canPreviewModel, false);
  const { text, loading: textLoading, failed: textFailed } = useTextContent(
    artifact.downloadUrl,
    canPreviewText,
  );

  // A failed download falls through to the icon placeholder — the same
  // failure UX the text branch already has — instead of "Loading 3D preview…"
  // forever. The artifact name stays a download link either way.
  const showModelPreview = !isImage && is3DModel && canPreviewModel && !modelFailed;
  const showPdfPreview = !isImage && !is3DModel && !!artifact.downloadUrl
    && canPreviewPdf(artifact.name, artifact.fileSize, artifact.contentType);

  // A CAD upload has no browser-renderable form until the runner derives one,
  // which happens shortly after the run starts. Show progress rather than a
  // static placeholder that silently pops into a viewer.
  const runActive = useOptionalEditorContext()?.isRunActive ?? false;
  const generatingPreview = usePreviewGenerationPending(
    !isImage
      && isCadFile(artifact.name)
      && !artifact.previewUrl
      && runActive,
  );

  const handleImageExpand = useCallback(() => setLightboxOpen(true), []);
  const handleModelExpand = useCallback(() => setModelModalOpen(true), []);

  return (
    <Wrap data-testid={dataTestId} data-artifact-id={artifact.artifactId}>
      {isImage && (
        <PreviewBox data-testid={`${dataTestId}__image-preview`}>
          <Thumb
            src={artifact.downloadUrl}
            alt={artifact.name}
            loading="lazy"
            data-testid={`${dataTestId}__thumb`}
            onClick={handleImageExpand}
          />
          <ExpandButton
            type="button"
            aria-label="Open image fullscreen"
            data-testid={`${dataTestId}__image-expand`}
            onClick={handleImageExpand}
          >
            <MdOpenInFull size={14} />
          </ExpandButton>
        </PreviewBox>
      )}

      {showModelPreview && (
        // `nowheel` and `nopan` let the inline viewer own wheel zoom and right-drag
        // pan instead of React Flow zooming/panning the graph (AIB-7279, AIB-8278).
        <PreviewBox className="nowheel nopan" data-testid={`${dataTestId}__model-preview`}>
          {blob ? (
            <ModelPreviewSurface>
              <RenderErrorBoundary
                label="ModelViewer"
                fileName={artifact.name}
                fallback={
                  <Placeholder data-testid={`${dataTestId}__placeholder`}>
                    {ICON_BY_TYPE[inferredType] ?? <MdDescription />}
                  </Placeholder>
                }
              >
                <ModelViewer
                  data={blob}
                  fileName={artifact.name}
                  loaderType={model.loaderType}
                  compact
                />
              </RenderErrorBoundary>
            </ModelPreviewSurface>
          ) : (
            <ModelLoadingSurface data-testid={`${dataTestId}__model-loading`}>
              Loading 3D preview…
            </ModelLoadingSurface>
          )}
          <ExpandButton
            type="button"
            aria-label="Open 3D viewer fullscreen"
            data-testid={`${dataTestId}__model-expand`}
            onClick={handleModelExpand}
            disabled={!blob}
          >
            <MdOpenInFull size={14} />
          </ExpandButton>
        </PreviewBox>
      )}

      {showPdfPreview && (
        <PreviewBox data-testid={`${dataTestId}__pdf-preview`}>
          <DocumentPreviewSurface>
            <Suspense fallback={null}>
              <PdfPreview
                url={artifact.downloadUrl}
                name={artifact.name}
                fileSize={artifact.fileSize}
              />
            </Suspense>
          </DocumentPreviewSurface>
        </PreviewBox>
      )}

      {!isImage && !showModelPreview && generatingPreview && (
        <ModelLoadingSurface data-testid={`${dataTestId}__preview-generating`}>
          Generating 3D preview…
        </ModelLoadingSurface>
      )}

      {!isImage && !showModelPreview && !generatingPreview
        && canPreviewText && !textFailed && (
        textLoading || text === null ? (
          <TextLoadingSurface data-testid={`${dataTestId}__text-loading`}>
            Loading preview…
          </TextLoadingSurface>
        ) : (
          // Read-only textarea so users can scroll / copy the value but
          // never edit it (artifacts are immutable). {@code readOnly} keeps
          // the element focusable and selectable; {@code disabled} would
          // strip both, which the design explicitly does not want.
          <TextPreviewBox
            data-testid={`${dataTestId}__text-preview`}
            value={text}
            readOnly
            aria-label={`Text content of ${artifact.name}`}
          />
        )
      )}

      {!isImage && !showModelPreview && !showPdfPreview && !generatingPreview
        && (!canPreviewText || textFailed) && (
        <Placeholder data-testid={`${dataTestId}__placeholder`}>
          {ICON_BY_TYPE[inferredType] ?? <MdDescription />}
        </Placeholder>
      )}

      <TopRow>
        <Name
          href={artifact.downloadUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={artifact.name}
          data-testid={`${dataTestId}__name`}
        >
          {artifact.name}
        </Name>
        <Meta data-testid={`${dataTestId}__size`}>
          {formatBytes(artifact.fileSize)}
        </Meta>
      </TopRow>

      {isImage && lightboxOpen && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={[{ src: artifact.downloadUrl, alt: artifact.name }]}
          plugins={[Zoom]}
          carousel={{ finite: true }}
          controller={{ closeOnBackdropClick: true }}
          render={{ buttonPrev: () => null, buttonNext: () => null }}
        />
      )}

      {is3DModel && modelModalOpen && blob && (
        <ModelViewerModal
          data={blob}
          fileName={artifact.name}
          loaderType={model.loaderType}
          onClose={() => setModelModalOpen(false)}
        />
      )}
    </Wrap>
  );
};

export default ArtifactPreview;
